import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = process.env.REACT_APP_GA4_ID;

let _injected = false;

/** Inject the gtag.js snippet once — no-op if REACT_APP_GA4_ID isn't set. */
const injectGtag = () => {
  if (_injected || !GA_ID) return;
  _injected = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  // Manual page_view firing per SPA route change — disable auto so we don't double-count.
  window.gtag("config", GA_ID, { send_page_view: false });
};

/**
 * Fires a GA4 `page_view` on every route change. Mount once inside <Router>.
 * If REACT_APP_GA4_ID is empty (dev / preview without a real key), this is a
 * pure no-op so we don't ship a phantom tracker.
 */
export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Inject the snippet on first render.
  useEffect(() => { injectGtag(); }, []);

  // Fire pageview every time the route changes.
  useEffect(() => {
    if (!GA_ID || !window.gtag) return;
    window.gtag("event", "page_view", {
      page_path: location.pathname + location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search]);
};

/**
 * Fire a custom GA4 event. Safe to call even when GA isn't configured.
 * Example: trackEvent("reveal_paid", { amount: 10, currency: "USD" });
 */
export const trackEvent = (name, params = {}) => {
  if (!GA_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
};

export default useGoogleAnalytics;
